import React, { useCallback, useEffect, useState } from "react";
import Layout from "../../Components/Layout";
import ProGrid from "../../Components/ProGrid/v2";
import usePageState from "../../../utils/customHooks/usePageState";
import useColumn from "../../../utils/customHooks/useColumns";
import { STATUS_MAPPINGS, STAUTES } from "../../../utils/constant";
import { useTranslation } from "react-i18next";
import { postApi, getApi } from "../../../utils/services";
import { GET_APPRENTICE_MENTORS, GET_APPRENTICE_FORM_STATUS } from "../../../utils/services/apiPath";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { loginSelector } from "../../store/features/auth/authSlice";
import { dateColumnDef } from "../../Components/ProGrid/components/FormatCell";
import RegistrationPendingModel from "../../Components/RegistrationPendingModel";
import { v4 as uuidV4 } from "uuid";
import { errorToast } from "../../../utils/toastHelper";
import { createODataQuery } from "../../../utils/services/oData";

export default function MyMentors() {
  const { t } = useTranslation();
  const { user } = useSelector(loginSelector);
  const [open, setOpen] = useState(false);
  const [pendingItems, setPendingItems] = useState([]);
  const [modalText, setModalText] = useState("");

  const {
    currentPage,
    pageSize,
    data: projectsData,
    rowCount,
    status,
    sortModel,
    searchString,
    setPageState,
    setPagination,
    setStatus,
    setSortModel,
    setSearchString
  } = usePageState();
  const navigate = useNavigate();

  const handleProfileUpdate = useCallback(async () => {
    try {
      const { documentStatus, isFormCompleted } = await getApi(`${GET_APPRENTICE_FORM_STATUS}/${user.employeeId}`);
      const tempPendingItems = Object.entries(STATUS_MAPPINGS)
        .filter(([key]) => documentStatus?.includes(key))
        .map(([, value]) => t(value));
      setPendingItems(tempPendingItems);
      const modalText =
        documentStatus?.length === 2 && documentStatus?.includes("RAP_NOT_ASSIGNED")
          ? t("ApprenticeRegistrationRAPPendingText")
          : t("ApprenticeRegistrationPendingText");
      setModalText(modalText);
      if (!isFormCompleted) setOpen(true);
    } catch (error) {
      errorToast(error);
    }
  }, [user.employeeId, t]);

  const columns = useColumn(
    [
      {
        field: "mentorFirstName",
        headerName: "MentorName",
        valueGetter: ({ row }) => `${row.mentorFirstName || ""} ${row.mentorLastName || ""}`
      },
      {
        field: "apprenticeFirstName",
        headerName: "ApprenticeName",
        valueGetter: ({ row }) => `${row.apprenticeFirstName || ""} ${row.apprenticeLastName || ""}`
      },
      {
        field: "projectName",
        headerName: "ProjectName"
      },
      dateColumnDef({
        field: "workDate",
        headerName: "WorkDate"
      })
    ],
    true
  );

  const getMentors = useCallback(async () => {
    setStatus(STAUTES.LOADING);

    const searchableFields = ["mentorFirstName", "mentorLastName", "apprenticeFirstName", "apprenticeLastName", "projectName"];
    const query = createODataQuery(searchableFields, [], "", searchString, sortModel, pageSize, currentPage);
    const response = await getApi(`${GET_APPRENTICE_MENTORS}${query}`);
    setStatus(STAUTES.IDLE);
    if (response?.error) return errorToast(response.error);
    const data = response?.value || [];
    const totalRecords = response?.["@odata.count"] ?? data.length;
    setPageState(prevPageInfo => ({ ...prevPageInfo, data: (data || []).map(rec => ({ ...rec, id: uuidV4() })), rowCount: totalRecords }));
  }, [currentPage, pageSize, sortModel, searchString, setPageState, setStatus]);

  useEffect(() => {
    handleProfileUpdate();
  }, []);

  useEffect(() => {
    getMentors();
  }, [getMentors]);

  return (
    <>
      <Layout>
        <ProGrid
          title={t("MyMentors")}
          columns={columns}
          loading={status === STAUTES.LOADING}
          rows={projectsData}
          searchMode={!!searchString}
          searchModel={{ defaultValue: searchString, handleDebounce: setSearchString }}
          hideAddButton={true}
          hideExport={false}
          options={{
            getRowId: row => row.id,
            rowCount: rowCount,
            paginationMode: "server",
            paginationModel: { pageSize: pageSize, page: currentPage },
            onPaginationModelChange: setPagination,
            sortingMode: "server",
            onSortModelChange: setSortModel
          }}
        />
      </Layout>

      {user?.isApprentice &&
        <RegistrationPendingModel
          open={open}
          title={t("ApprenticeRegistrationPendingTitle")}
          message={modalText}
          pendingItems={pendingItems}
          onConfirm={() => navigate("/registration")}
          confirmBtnText={t("Ok")}
        />
      }
    </>
  );
}
